import { Trans, useTranslation } from 'react-i18next';
import { useBoolean, useUpdateEffect } from 'ahooks';
import { Checkbox, FormControlLabel, Link, Typography, Unstable_Alert2 } from '@mui/material';
import { PasswordStrength } from '@/components/biz/password-strength';
import { usePwdStrength } from '@/components/biz/password-strength/use-pwd-strength';
import { FormText } from '@/components/form/form-text';
import { StepBox } from '@/components/layout/step-box';
import { Continue } from '@/components/ui/continue';
import { ErrorCode } from '@/constants/error';
import { Flow } from '@/constants/flow';
import { useBizState } from '@/hooks/use-biz-state';
import { useCore } from '@/hooks/use-core';
import { useCoreRequest } from '@/hooks/use-core-request';
import { useFidoSkip } from '@/hooks/use-fido-skip';
import { useLidForm } from '@/hooks/use-lid-form';
import { useConfigStore } from '@/store/use-config-store';

interface SetupPasswordProps {
  type: 'register' | 'reset' | 'recovery';
}

interface FormData {
  password: string;
  confirm: string;
}

export const StepSetupPassword: React.FC<SetupPasswordProps> = ({ type }) => {
  const { lang, lenovoId, setFlow } = useCore();
  const et = useConfigStore((s) => s.et);
  const { userName, areaCode, firstName, lastName, country, authcode, supportPassKey } = useBizState();
  const { t } = useTranslation();
  const { control, clearErrors, watch, getValues, setError, handleSubmit } = useLidForm<FormData>({
    defaultValues: { password: '', confirm: '' },
    mode: 'onChange',
  });
  const { setFidoSkip } = useFidoSkip();

  const pwd = watch('password');

  const [visible, { toggle }] = useBoolean(false);
  const { validatePwd, strength } = usePwdStrength();

  useUpdateEffect(() => {
    const pwd2 = getValues('confirm');
    if (pwd === pwd2 && pwd2) clearErrors('confirm');
  }, [pwd]);

  const mapper = {
    register: {
      request: (data: FormData) =>
        lenovoId.registerVerifyPassword({
          userName,
          firstName,
          lastName,
          areaCode,
          password: data.password,
          et,
          bid: window.bid,
          verifyCode: authcode,
          location: country,
          lang,
          supportPassKey,
        }),
      onSuccess: ({ data, nextSteps }: CoreRes) => {
        // should reset skip fido flag to avoid affecting later flow
        const { authType, ...rest } = data;
        setFidoSkip(false);

        setFlow(Flow.REGISTER_STEP_ALL_DONE, {
          state: {
            setupPwdDoneNextStep: nextSteps,
            oauthGrantAuthType: authType,
            ...rest,
            // openApp: !!data.openApp,
            // callBack: data.callBack,
            // appName: data.appName,
            // enablePassKey: data.enablePassKey,
            // supportPassKey: data.supportPassKey,
          },
        });
      },
    },
    reset: {
      request: (data: FormData) =>
        lenovoId.resetPasswordVerifyPassword({
          userName,
          areaCode,
          password: data.password,
          et,
          verifyCode: authcode,
          lang,
          bid: window.bid,
        }),
      onSuccess: () => setFlow(Flow.RESET_PASSWORD_STEP_COMPLETE),
    },
    recovery: {
      request: (data: FormData) =>
        lenovoId.recoveryVerifyPassword({
          userName,
          areaCode,
          password: data.password,
          et,
          verifyCode: authcode,
          lang,
          bid: window.bid,
        }),
      onSuccess: () => setFlow(Flow.RECOVERY_STEP_COMPLETE),
    },
  };

  const { run, loading } = useCoreRequest(mapper[type].request, {
    manual: true,
    onSuccess: mapper[type].onSuccess,
    onError(code) {
      switch (code) {
        case ErrorCode.InvalidPasswordFormat:
          setError('password', t('password_format_error'));
          return true;

        default:
          break;
      }
    },
  });

  const title = type === 'register' ? t('lenovo_account_creation_title') : t('password_reset_title');

  return (
    <StepBox title={title}>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit(run)} noValidate>
        <FormText
          name="password"
          control={control}
          rules={{ validate: validatePwd }}
          type={visible ? 'text' : 'password'}
          label={t('password')}
        />

        <FormText
          name="confirm"
          control={control}
          rules={{
            required: t('password_confirmation_required'),
            validate: (v, { password }) => {
              return v === password || t('password_mismatch_error');
            },
          }}
          type={visible ? 'text' : 'password'}
          label={t('password_confirmation_label')}
        />

        <div>
          <FormControlLabel control={<Checkbox value={visible} onChange={toggle} />} label={t('show_password')} />
        </div>

        <Unstable_Alert2 severity="info">{t('password_rules_description')}</Unstable_Alert2>

        <PasswordStrength strength={strength} />

        <Typography variant="body2">
          <Trans i18nKey="privacy_terms_agreement">
            <Link target="_blank" rel="noopener noreferrer" href={t('privacy_url')}></Link>
            <Link target="_blank" rel="noopener noreferrer" href={t('legal_url')}></Link>
          </Trans>
        </Typography>

        <Continue loading={loading} />
      </form>
    </StepBox>
  );
};
